// web/src/components/format.ts
//
// Locale-aware UI formatting helpers. Each user-facing helper accepts an
// optional `locale` argument that defaults to the active i18next language.
// Callers that need a different locale (e.g. a unit test, or a deliberate
// formatting choice such as the stable yyyy-mm-dd grouping key in
// calendar/dateUtils.ts) pass an explicit value.
//
// Use `Intl.*` rather than hand-rolled "5s ago" strings so the output
// matches the user's selected language and number formatting conventions
// without per-translator string maintenance.
import i18n from "@/i18n";

function activeLocale(locale?: string): string {
  return locale || i18n.language || "en";
}

export function relativeTime(iso: string | null | undefined, locale?: string): string {
  if (!iso) return "—";
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return iso;
  const diffSeconds = Math.round((Date.now() - t) / 1000);
  const rtf = new Intl.RelativeTimeFormat(activeLocale(locale), {
    numeric: "auto",
    style: "short",
  });
  if (diffSeconds < 60) return rtf.format(-diffSeconds, "second");
  const diffMinutes = Math.round(diffSeconds / 60);
  if (diffMinutes < 60) return rtf.format(-diffMinutes, "minute");
  const diffHours = Math.round(diffMinutes / 60);
  if (diffHours < 24) return rtf.format(-diffHours, "hour");
  const diffDays = Math.round(diffHours / 24);
  return rtf.format(-diffDays, "day");
}

export function safeHref(url: string | undefined | null): string | undefined {
  if (!url) return undefined;
  try {
    const u = new URL(url);
    if (u.protocol === "http:" || u.protocol === "https:") return u.toString();
  } catch {
    // not a URL
  }
  return undefined;
}

/**
 * The structured bias enum the collector emits as `directional_bias.bias`.
 * Mirrors web/public/locales/en/enums.json[bias]. The provider-facing API
 * is moving toward shipping this code alongside the localized `label`.
 */
export type BiasEnum =
  | "bullish_strong"
  | "bullish"
  | "neutral_to_bullish"
  | "neutral"
  | "neutral_to_bearish"
  | "bearish"
  | "bearish_strong";

/**
 * `biasTone` chooses the dashboard colour (bull/bear/neutral) for a
 * directional-bias signal.
 *
 * IMPORTANT (C4):
 * The original implementation substring-matched the English `label`
 * ("Bullish", "Bearish", …). Once translated UI lights up in Slice 2 the
 * label localizes (e.g. Turkish "yükseliş eğiliminde") and the sniff
 * silently misroutes — the badge flips wrong-colour. We now accept either:
 *
 *   1. A structured `{ bias }` (preferred) — the backend's enum code,
 *      independent of language. Routed by switch.
 *   2. A bare string OR `{ label }` without enum (legacy callers / older
 *      payloads). Falls through to the English-keyword sniff and is
 *      marked TODO for removal once the enum is universal.
 *
 * The fallback branch is preserved for now because the OpenAPI surface
 * doesn't yet carry the enum on every analysis payload (mirrors what F7
 * did for `driverArrowFromDirection`).
 */
export type BiasToneInput = string | { bias?: BiasEnum | string; label?: string };

export function biasTone(input: BiasToneInput): "bull" | "bear" | "neutral" {
  // 1. Structured enum input — language-independent, takes priority.
  if (typeof input === "object" && input !== null) {
    const code = input.bias;
    if (code) {
      switch (code) {
        case "bullish":
        case "bullish_strong":
        case "neutral_to_bullish":
          return "bull";
        case "bearish":
        case "bearish_strong":
        case "neutral_to_bearish":
          return "bear";
        case "neutral":
          return "neutral";
      }
    }
    // No enum on the struct — sniff the label.
    return sniffBiasLabel(input.label ?? "");
  }
  // 2. Legacy bare-string callers — TODO: drop once API guarantees enum.
  return sniffBiasLabel(input);
}

function sniffBiasLabel(label: string): "bull" | "bear" | "neutral" {
  const l = label.toLowerCase();
  if (l.startsWith("neutral_to_bullish")) return "bull";
  if (l.startsWith("neutral_to_bearish")) return "bear";
  if (l.includes("bullish")) return "bull";
  if (l.includes("bearish")) return "bear";
  return "neutral";
}

export function clockTime(iso: string | null | undefined, locale?: string): string {
  if (!iso) return "--:--";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "--:--";
  return new Intl.DateTimeFormat(activeLocale(locale), {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(date);
}

export function countdown(targetIso: string | null | undefined, now = Date.now()): string {
  if (!targetIso) return "--";
  const target = Date.parse(targetIso);
  if (Number.isNaN(target)) return "--";
  const remaining = Math.max(0, Math.round((target - now) / 1000));
  const minutes = Math.floor(remaining / 60);
  const seconds = remaining % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

export function percent(value: number, locale?: string): string {
  const fmt = new Intl.NumberFormat(activeLocale(locale), {
    style: "percent",
    maximumFractionDigits: 0,
  });
  return fmt.format(Math.max(0, Math.min(1, value)));
}
